import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';

interface IWhitelistContext {
    whitelistPhones: string[],
    whitelistEmails: string[],
    whitelistAddresses: string[],
    isEmailWhitelisted: (email: string) => boolean,
    isPhoneWhitelisted: (phone: string) => boolean,
    isAddressWhitelisted: (address: string) => boolean,
    isSsnWhitelisted: (ssn: string) => boolean,
};

const WhitelistContext = createContext<IWhitelistContext>({
    whitelistPhones: [],
    whitelistEmails: [],
    whitelistAddresses: [],
    isEmailWhitelisted: () => false,
    isPhoneWhitelisted: () => false,
    isAddressWhitelisted: () => false,
    isSsnWhitelisted: () => false,
});

export const useWhitelistContext = () => useContext(WhitelistContext);

export default function WhitelistProvider({
    children,
}: PropsWithChildren<{}>) {
    const { whitelistAddresses, whitelistEmails, whitelistPhones, whitelistSsns } = 
    Object.fromEntries(
        Object.entries(
            JSON.parse(PathUtils.getMetaPropertyValue('whitelistContent') || '{}') as Record<string, string[]>,
        ).map(([i, val]) => [i, val.map(atob)]),
    );

    const isEmailWhitelisted = (email: string): boolean => {
        return whitelistEmails.some((e: string) => e.toLowerCase() === email.toLowerCase());
    };

    const isPhoneWhitelisted = (phone: string): boolean => {
        return whitelistPhones.some((p) => p.replace(/\D/g, '') === phone.replace(/\D/g, ''));
    };

    const isAddressWhitelisted = (address: string): boolean => {
        return whitelistAddresses.some((a) => a.toLowerCase().replace(/\s/g, '') === address.toLowerCase().replace(/\s/g, ''));
    };

    const isSsnWhitelisted = (ssn: string): boolean => {
        return whitelistSsns.some((s) => 
            s.toLowerCase().replace(/\D/g, '').includes(ssn.toLowerCase().replace(/\D/g, ''))
            || ssn.toLowerCase().replace(/\D/g, '').startsWith(s.toLowerCase().replace(/\D/g, ''))
        );
    };

    return (
        <WhitelistContext.Provider value={useMemo(() => {
            return {
                whitelistEmails,
                whitelistPhones,
                whitelistAddresses,
                whitelistSsns,
                isEmailWhitelisted,
                isPhoneWhitelisted,
                isAddressWhitelisted,
                isSsnWhitelisted
            };
        }, [whitelistAddresses, whitelistEmails, whitelistPhones])}>
            {children}
        </WhitelistContext.Provider>
    );
}